import { useState, useCallback } from 'react';

// Template URLs served from backend API (avoids CORS issues)
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TEMPLATE_SQUARE = `${API_URL}/api/templates/wisdom-square.png`;
const TEMPLATE_STORY = `${API_URL}/api/templates/wisdom-story.png`;

const INSTAGRAM_HANDLE = "@sophia_einstein_the_wise";

// Exact white content area coordinates (measured from templates)
const CONTENT_AREAS = {
  square: {
    // Template: 1000x1000
    x: 202,
    y: 218,
    width: 595,
    height: 564,
  },
  story: {
    // Template: 720x1280
    x: 133,
    y: 202,
    width: 454,
    height: 874,
  }
};

/**
 * Hook for generating shareable wisdom card images using Canvas API
 * Uses custom Canva-designed templates as backgrounds
 */
export function useWisdomCard() {
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Load image as promise
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };
  
  // Wrap text to fit within maxWidth, returns lines array
  const wrapText = (ctx, text, maxWidth, fontSize) => {
    ctx.font = `italic ${fontSize}px "Playfair Display", Georgia, serif`;
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };
  
  // Calculate optimal font size to fit text in available height
  const calculateOptimalFontSize = (ctx, text, maxWidth, maxHeight, startFontSize = 36, minFontSize = 18) => {
    let fontSize = startFontSize;
    const lineHeightRatio = 1.45;
    
    while (fontSize >= minFontSize) {
      const lines = wrapText(ctx, text, maxWidth, fontSize);
      const lineHeight = fontSize * lineHeightRatio;
      const totalHeight = lines.length * lineHeight;
      
      if (totalHeight <= maxHeight) {
        return { fontSize, lines, lineHeight, totalHeight };
      }
      
      fontSize -= 2;
    }
    
    // Return minimum size if nothing fits
    const lines = wrapText(ctx, text, maxWidth, minFontSize);
    const lineHeight = minFontSize * lineHeightRatio;
    return { 
      fontSize: minFontSize, 
      lines, 
      lineHeight, 
      totalHeight: lines.length * lineHeight 
    };
  };
  
  /**
   * Generate a wisdom card image using the Canva template
   * @param {string} quote - The quote text to display
   * @param {string} format - 'square' (1:1) or 'story' (9:16)
   * @returns {Promise<string>} - Data URL of the generated image
   */
  const generateCard = useCallback(async (quote, format = 'square') => {
    setIsGenerating(true);
    
    try {
      const isStory = format === 'story';
      const templateUrl = isStory ? TEMPLATE_STORY : TEMPLATE_SQUARE;
      const contentArea = isStory ? CONTENT_AREAS.story : CONTENT_AREAS.square;
      
      // Load the template image
      const templateImg = await loadImage(templateUrl);
      
      // Use template's natural dimensions
      const width = templateImg.naturalWidth;
      const height = templateImg.naturalHeight;
      
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Draw the template as background
      ctx.drawImage(templateImg, 0, 0, width, height);
      
      // CSS-like padding: 10% top/bottom, 15% left/right
      const paddingX = contentArea.width * 0.12; // 12% horizontal padding
      const paddingTop = contentArea.height * 0.08; // 8% top padding
      const paddingBottom = contentArea.height * 0.08; // 8% bottom padding
      
      // Calculate the centered text area within the white content box
      const textAreaX = contentArea.x + paddingX;
      const textAreaY = contentArea.y + paddingTop;
      const textAreaWidth = contentArea.width - (paddingX * 2);
      const textAreaHeight = contentArea.height - paddingTop - paddingBottom;
      
      // Reserve space for header and signature
      const headerHeight = isStory ? 55 : 50;
      const signatureHeight = isStory ? 75 : 65;
      
      // Available height for the quote (middle section)
      const quoteAreaTop = textAreaY + headerHeight;
      const quoteAreaHeight = textAreaHeight - headerHeight - signatureHeight;
      
      // "Today's Wisdom" header - centered within text area
      const headerY = textAreaY + 35;
      ctx.fillStyle = '#F4A460'; // Sandy brown for sun
      ctx.font = `${isStory ? 26 : 24}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText('☀', textAreaX, headerY);
      
      ctx.fillStyle = '#555555';
      ctx.font = `bold ${isStory ? 24 : 22}px "Playfair Display", Georgia, serif`;
      ctx.fillText("Today's Wisdom", textAreaX + 32, headerY);
      
      // Quote text - add quotes around it
      const quoteText = `"${quote}"`;
      
      // Calculate optimal font size to fit the quote in available space
      const startFontSize = isStory ? 30 : 28;
      const minFontSize = isStory ? 16 : 14;
      const { fontSize, lines, lineHeight, totalHeight } = calculateOptimalFontSize(
        ctx, 
        quoteText, 
        textAreaWidth,
        quoteAreaHeight,
        startFontSize,
        minFontSize
      );
      
      // Center the quote vertically within the quote area
      const quoteStartY = quoteAreaTop + (quoteAreaHeight - totalHeight) / 2 + lineHeight * 0.75;
      
      // Draw quote text - left aligned within the centered block
      ctx.fillStyle = '#333333';
      ctx.font = `italic ${fontSize}px "Playfair Display", Georgia, serif`;
      ctx.textAlign = 'left';
      
      lines.forEach((line, i) => {
        const lineY = quoteStartY + (i * lineHeight);
        // Only draw if within bounds
        if (lineY < contentArea.y + contentArea.height - signatureHeight - paddingBottom) {
          ctx.fillText(line, textAreaX, lineY);
        }
      });
      
      // Signature at the bottom - same left margin as text
      const signatureY = contentArea.y + contentArea.height - paddingBottom - 40;
      ctx.fillStyle = '#E91E8C'; // Pink color
      ctx.font = `bold ${isStory ? 22 : 20}px "Playfair Display", Georgia, serif`;
      ctx.textAlign = 'left';
      ctx.fillText('— Sophia Einstein', textAreaX, signatureY);
      
      // Instagram handle - same left margin
      ctx.fillStyle = '#888888';
      ctx.font = `${isStory ? 18 : 16}px Arial, sans-serif`;
      ctx.fillText(INSTAGRAM_HANDLE, textAreaX, signatureY + (isStory ? 28 : 24));
      
      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/png', 0.95);
      setIsGenerating(false);
      return dataUrl;
      
    } catch (error) {
      console.error('Error generating wisdom card:', error);
      setIsGenerating(false);
      throw error;
    }
  }, []);
  
  /**
   * Generate card and trigger download
   */
  const downloadCard = useCallback(async (quote, format = 'square', filename = 'sophia-wisdom') => {
    const dataUrl = await generateCard(quote, format);
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${filename}-${format}.png`;
    link.click();
    return dataUrl;
  }, [generateCard]);
  
  /**
   * Generate card and share via Web Share API
   */
  const shareCard = useCallback(async (quote, format = 'square') => {
    const dataUrl = await generateCard(quote, format);
    
    // Convert data URL to blob for sharing
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], 'sophia-wisdom.png', { type: 'image/png' });
    
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Sophia Einstein Wisdom',
          text: `"${quote}" — Sophia Einstein ${INSTAGRAM_HANDLE}`,
        });
        return true;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share failed, falling back to download');
          await downloadCard(quote, format);
        }
        return false;
      }
    } else {
      // Fallback to download
      await downloadCard(quote, format);
      return false;
    }
  }, [generateCard, downloadCard]);
  
  return {
    isGenerating,
    generateCard,
    downloadCard,
    shareCard,
  };
}
